import { QUESTION_TYPES } from '@api/instant_survey';

/**
 * Core utility to filter question summaries by compareBy - matches exact UI logic
 * This is the base function used by all other filtering functions to ensure consistency
 *
 * @param {Object} params - Filter parameters
 * @param {boolean} params.areFiltersApplied - Whether any filters are currently applied
 * @param {Object} params.question - Question object with id
 * @param {Object} params.selectedCompareBy - Selected comparison option with value property
 * @param {Object} params.surveyResponseSummariesMap - Map containing question summaries
 * @returns {Array|null} - Filtered summaries array or null
 */
export const getBaseSummariesByCompareBy = ({
  areFiltersApplied = false,
  question,
  selectedCompareBy,
  surveyResponseSummariesMap,
}) => {
  // Don't return summaries if filters are applied
  if (areFiltersApplied) return null;

  // Get summaries for the question, filtered by compareBy only (matches UI logic exactly)
  const summaries = surveyResponseSummariesMap.questionSummaries?.[String(question.id)]?.filter(
    ({ additionalParams }) => additionalParams.compareBy === selectedCompareBy?.value
  );

  return summaries?.length > 0 ? summaries : null;
};

/**
 * Gets the summary or key findings for a question using exact UI logic
 * Handles both ESSAY and other question types consistently
 *
 * @param {Object} params - Filter parameters
 * @param {boolean} params.areFiltersApplied - Whether any filters are currently applied
 * @param {Object} params.question - Question object with id and type
 * @param {Object} params.selectedCompareBy - Selected comparison option with value property
 * @param {Object} params.segment - Segment data (for ESSAY questions)
 * @param {Object} params.surveyResponseSummariesMap - Map containing question summaries
 * @returns {Object|null} - Single summary object or null
 */
export const getSummaryOrFindings = ({
  areFiltersApplied = false,
  question,
  segment = null,
  selectedCompareBy,
  surveyResponseSummariesMap,
}) => {
  const summaries = getBaseSummariesByCompareBy({
    areFiltersApplied,
    question,
    selectedCompareBy,
    surveyResponseSummariesMap,
  });

  if (!summaries?.length) return null;

  // Handle ESSAY questions differently
  if (question.type === QUESTION_TYPES.ESSAY.value) {
    const result = summaries.find(({ additionalParams }) => {
      // Return overall if compare_by is overall else find the matching quota group name
      return selectedCompareBy.value === 'overall'
        ? true
        : additionalParams.compareByName === segment?.id;
    });

    return result || null;
  }

  // For other question types, return the first result
  return Array.isArray(summaries) && summaries.length > 0 ? summaries[0] : null;
};

/**
 * Gets numeric key findings for PowerPoint export using EXACT same logic as UI
 * No fallback logic - only show key findings that UI would show
 *
 * @param {Object} params - Filter parameters
 * @param {boolean} params.areFiltersApplied - Whether any filters are currently applied
 * @param {Object} params.question - Question object with id
 * @param {Object} params.selectedCompareBy - Selected comparison option with value property
 * @param {Object} params.surveyResponseSummariesMap - Map containing question summaries
 * @returns {Array|null} - Array of numeric findings or null
 */
export const getNumericKeyFindingsForPowerPoint = ({
  areFiltersApplied = false,
  question,
  selectedCompareBy,
  surveyResponseSummariesMap,
}) => {
  if (areFiltersApplied) return null;

  const summaries = surveyResponseSummariesMap.questionSummaries?.[String(question.id)]
    ?.filter(({ additionalParams }) => additionalParams.compareBy === selectedCompareBy?.value)
    ?.filter((finding) => finding?.reportType === 'KEY_NUMERIC_FINDINGS_QUESTION');

  return summaries?.length ? [summaries[0]] : null;
};

/**
 * Gets text summaries for PowerPoint export using EXACT same logic as UI
 * Filters for OPEN_END_QUESTION type summaries
 *
 * @param {Object} params - Filter parameters
 * @param {boolean} params.areFiltersApplied - Whether any filters are currently applied
 * @param {Object} params.question - Question object with id
 * @param {Object} params.selectedCompareBy - Selected comparison option with value property
 * @param {Object} params.surveyResponseSummariesMap - Map containing question summaries
 * @returns {Array|null} - Array of text summaries or null
 */
export const getTextSummariesForPowerPoint = ({
  areFiltersApplied = false,
  question,
  selectedCompareBy,
  surveyResponseSummariesMap,
}) => {
  if (areFiltersApplied) return null;

  const summaries = surveyResponseSummariesMap.questionSummaries?.[String(question.id)]
    ?.filter(({ additionalParams }) => additionalParams.compareBy === selectedCompareBy?.value)
    ?.filter(({ reportType }) => reportType === 'OPEN_END_QUESTION');

  return summaries?.length > 0 ? summaries : null;
};
